import React, { useEffect, useRef, useState } from "react";
import "../../styles/header.css";
import {
  FiChevronDown,
  FiLogOut,
  FiPackage,
  FiSearch,
  FiShoppingCart,
  FiUser,
  FiX,
} from "react-icons/fi";
import axios from "axios";
import logo from "../../assets/images/logo.png";
import { Link, useNavigate } from "react-router-dom";
import Constant from "../../helper/Constant";

const getCartHeaders = () => {
  const storedToken = localStorage.getItem("token");

  if (storedToken) {
    return { Authorization: storedToken };
  }

  return {};
};

const getCartParams = () => {
  const storedToken = localStorage.getItem("token");
  const guestCartId = localStorage.getItem("guestCartId");

  return storedToken || !guestCartId ? {} : { guest_id: guestCartId };
};

const Header = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [activeAccountTab, setActiveAccountTab] = useState("profile");
  const [profileDetails, setProfileDetails] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);

  const userMenuRef = useRef(null);
  const accountPanelRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    getCartCount();

    window.addEventListener("cartUpdated", getCartCount);

    return () => {
      window.removeEventListener("cartUpdated", getCartCount);
    };
  }, []);

  useEffect(() => {
    getAllCategory();

    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const clickedInsideMenu =
        userMenuRef.current && userMenuRef.current.contains(event.target);
      const clickedInsidePanel =
        accountPanelRef.current && accountPanelRef.current.contains(event.target);
      const clickedInsideSearch =
        searchRef.current && searchRef.current.contains(event.target);

      if (
        !clickedInsideMenu &&
        !clickedInsidePanel
      ) {
        setShowDropdown(false);
      }

      if (!clickedInsideSearch) {
        setShowSearchSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.get(
          `${Constant.postUrls.getAllProductsList}?page=1&limit=6&search=${encodeURIComponent(searchTerm.trim())}`
        );

        setSearchSuggestions(response?.data?.data?.docs || []);
        setShowSearchSuggestions(true);
      } catch {
        setSearchSuggestions([]);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("firebase_uid");
    setShowDropdown(false);
    setShowAccountPanel(false);
    window.location.href = "/";
  };

  const getCartCount = async () => {
    try {
      const cartHeaders = getCartHeaders();

      if (!cartHeaders) {
        setCartCount(0);
        return;
      }

      const res = await axios.get(Constant.postUrls.getMyCartItems, {
        headers: cartHeaders,
        params: getCartParams(),
      });

      const items = res?.data?.data?.docs || [];
      const totalQty = items.reduce(
        (total, item) => total + (item.quantity || 1),
        0
      );

      setCartCount(totalQty);
    } catch {
      setCartCount(0);
    }
  };

  const getAllCategory = async () => {
    try {
      const response = await axios.get(
        Constant.getUrls.getAllCategories + "?page=1&limit=100"
      );
      setAllCategories(
        (response.data.data.docs || []).filter(
          (item) =>
            item.title?.toLowerCase() !== "custom miniature" &&
            item.status?.toLowerCase() !== "inactive"
        )
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProfileDetails = async () => {
    if (!token) return;

    try {
      setLoadingProfile(true);
      setAccountError("");

      const response = await axios.get(Constant.getUrls.getUserDetails, {
        headers: { Authorization: token },
      });
      console.log(response);

      const data = response?.data?.data || null;
      setProfileDetails(data);
      console.log(data);

      if (data) {
        setUser(data);
        localStorage.setItem("user", JSON.stringify(data));
      }
    } catch (error) {
      setAccountError(
        error?.response?.data?.message || "Failed to load profile details."
      );
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchMyOrders = async () => {
    if (!token) return;

    try {
      setLoadingOrders(true);
      setAccountError("");

      const response = await axios.get(Constant.getUrls.getMyOrders, {
        headers: { Authorization: token },
      });

      const orderData = response?.data?.data || [];
      setOrders(orderData);
      setExpandedOrderId(orderData[0]?._id || null);
    } catch (error) {
      setAccountError(
        error?.response?.data?.message || "Failed to load your orders."
      );
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const openAccountPanel = async (tab) => {
    setActiveAccountTab(tab);
    setShowDropdown(false);
    setShowAccountPanel(true);
    setAccountError("");

    if (tab === "profile") {
      await fetchProfileDetails();
    }

    if (tab === "orders") {
      await fetchMyOrders();
    }
  };

  const toggleUserDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const redirectToProductResults = (term, product = null) => {
    const trimmedTerm = term.trim();

    if (!trimmedTerm) return;

    const params = new URLSearchParams();
    const matchedCategory =
      product?.category ||
      allCategories.find((item) => {
        const title = String(item?.title || "").toLowerCase();
        const search = trimmedTerm.toLowerCase();
        return title.includes(search) || search.includes(title);
      });

    if (matchedCategory?._id) {
      params.set("catId", matchedCategory._id);
    }

    params.set("search", product?.name || trimmedTerm);

    setShowSearchSuggestions(false);
    navigate(`/products?${params.toString()}`);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const trimmedSearch = searchTerm.trim();
    const exactMatch = searchSuggestions.find(
      (item) => item?.name?.toLowerCase() === trimmedSearch.toLowerCase()
    );

    redirectToProductResults(trimmedSearch, exactMatch || null);
  };

  const closeAccountPanel = () => {
    setShowAccountPanel(false);
  };

  const formatCurrency = (amount) => {
    const value = Number(amount || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const renderProfileContent = () => {
    const details = profileDetails || user;

    if (loadingProfile) {
      return <p className="account-empty">Loading profile details...</p>;
    }

    if (!details) {
      return <p className="account-empty">No profile details found.</p>;
    }

    return (
      <div className="account-details-grid">
        <div className="account-detail-card">
          <span className="account-detail-label">Name</span>
          <strong>{details.name || "-"}</strong>
        </div>
        <div className="account-detail-card">
          <span className="account-detail-label">Mobile</span>
          <strong>{details.mobile_no || "-"}</strong>
        </div>
        <div className="account-detail-card">
          <span className="account-detail-label">Email</span>
          <strong>{details.email || "-"}</strong>
        </div>
        <div className="account-detail-card">
          <span className="account-detail-label">Address</span>
          <strong>{details.address || "-"}</strong>
        </div>
      </div>
    );
  };

  const renderOrderItems = (order) => {
    const items = order?.cart_id || [];

    if (!items.length) {
      return <p className="account-empty">No order items found.</p>;
    }

    return (
      <div className="order-items">
        {items.map((item) => {
          const productName =
            item?.variant_id?.product_id?.name ||
            item?.product_id?.name ||
            order?.product_name ||
            "Product";

          const quantityEntry = (order?.product_data || []).find(
            (entry) => entry?.cart_id === item?._id
          );

          return (
            <div className="order-item-row" key={item?._id}>
              <div>
                <div className="order-item-name">{productName}</div>
                <div className="order-item-meta">
                  Qty: {quantityEntry?.quantity || item?.quantity || 1}
                </div>
              </div>
              <div className="order-item-meta">
                {item?.variant_id?.colour || item?.variant_id?.size
                  ? `${item?.variant_id?.colour || ""} ${
                      item?.variant_id?.size || ""
                    }`.trim()
                  : ""}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrdersContent = () => {
    if (loadingOrders) {
      return <p className="account-empty">Loading your orders...</p>;
    }

    if (!orders.length) {
      return <p className="account-empty">No orders found for this account.</p>;
    }

    return (
      <div className="orders-list">
        {orders.map((order) => {
          const isExpanded = expandedOrderId === order._id;

          return (
            <div className="order-card" key={order._id}>
              <button
                type="button"
                className="order-card-header"
                onClick={() =>
                  setExpandedOrderId((prev) =>
                    prev === order._id ? null : order._id
                  )
                }
              >
                <div>
                  <div className="order-title">
                    Order #{String(order._id).slice(-8).toUpperCase()}
                  </div>
                  <div className="order-meta-line">
                    {formatDate(order.createdAt || order.order_date)} |{" "}
                    {order.order_status?.replaceAll("_", " ") || "-"}
                  </div>
                </div>
                <div className="order-amount">
                  {formatCurrency(order.order_amount)}
                </div>
              </button>

              {isExpanded && (
                <div className="order-card-body">
                  <div className="order-summary-grid">
                    <div>
                      <span className="account-detail-label">Payment</span>
                      <strong>
                        {order.payment_mode || "-"} / {order.payment_status || "-"}
                      </strong>
                    </div>
                    <div>
                      <span className="account-detail-label">Delivery Charge</span>
                      <strong>{formatCurrency(order.delivery_charge)}</strong>
                    </div>
                    <div>
                      <span className="account-detail-label">Mobile</span>
                      <strong>{order.mobile_no || user?.mobile_no || "-"}</strong>
                    </div>
                    <div>
                      <span className="account-detail-label">Address</span>
                      <strong>
                        {[
                          order?.address?.address,
                          order?.address?.city,
                          order?.address?.state,
                          order?.address?.pincode,
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </strong>
                    </div>
                  </div>

                  {renderOrderItems(order)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <header className="header">
        <div className="header-top">
          <Link to="/">
            <div className="logo">
              <img src={logo} alt="Logo" />
            </div>
          </Link>

          <div className="search-wrapper" ref={searchRef}>
            <form className="search-box" onSubmit={handleSearchSubmit}>
              <FiSearch />
              <input
                type="text"
                placeholder="Search Products"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  if (searchSuggestions.length) {
                    setShowSearchSuggestions(true);
                  }
                }}
              />
            </form>

            {showSearchSuggestions && searchSuggestions.length > 0 ? (
              <div className="search-suggestions">
                {searchSuggestions.map((item) => (
                  <button
                    type="button"
                    key={item._id}
                    className="search-suggestion-item"
                    onClick={() => {
                      setSearchTerm(item.name || "");
                      redirectToProductResults(item.name || "", item);
                    }}
                  >
                    <span className="search-suggestion-name">{item.name}</span>
                    <span className="search-suggestion-meta">
                      {item?.category?.title || "Product"}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="header-actions">
            {token && user ? (
              <div className="user-wrapper" ref={userMenuRef}>
                <button
                  type="button"
                  className="user-capsule"
                  onClick={toggleUserDropdown}
                >
                  <FiUser className="header-icon" />
                  <span className="user-name">
                    Hello, {user.name || user.mobile_no}
                  </span>
                  <FiChevronDown className="header-icon dropdown-arrow" />
                </button>

                {showDropdown && (
                  <div className="account-dropdown">
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => openAccountPanel("profile")}
                    >
                      <FiUser />
                      <span>Profile</span>
                    </button>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => openAccountPanel("orders")}
                    >
                      <FiPackage />
                      <span>My Orders</span>
                    </button>
                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={handleLogout}
                    >
                      <FiLogOut />
                      <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <button className="auth-btn">
                  <FiUser className="header-icon" />
                  <span className="login-text">Sign In</span>
                </button>
              </Link>
            )}

            <Link to="/cart" style={{ textDecoration: "none" }}>
              <button className="cart-btn" style={{ position: "relative" }}>
                <FiShoppingCart className="header-icon" />
                <span className="cart-text">Cart</span>
                {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
              </button>
            </Link>
          </div>
        </div>

        <div className="header-divider"></div>

        <div className="header-categories">
          {allCategories.map((item) => (
            <Link
              key={item._id}
              to={`/products?catId=${item._id}`}
              style={{ textDecoration: "none" }}
            >
              <button className="category-btn">
                <img
                  src={Constant.imageDomain + item.image}
                  className="category-image"
                  alt=""
                />
                {item.title}
              </button>
            </Link>
          ))}
        </div>

        <div className="header-divider"></div>
      </header>

      {showAccountPanel && (
        <div className="account-panel-overlay" onClick={closeAccountPanel}>
          <div
            className="account-panel"
            ref={accountPanelRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="account-panel-header">
              <div>
                <h3>My Account</h3>
                <p>Manage your profile and orders</p>
              </div>
              <button
                type="button"
                className="account-close-btn"
                onClick={closeAccountPanel}
              >
                <FiX />
              </button>
            </div>

            <div className="account-panel-tabs">
              <button
                type="button"
                className={activeAccountTab === "profile" ? "active" : ""}
                onClick={() => openAccountPanel("profile")}
              >
                Profile
              </button>
              <button
                type="button"
                className={activeAccountTab === "orders" ? "active" : ""}
                onClick={() => openAccountPanel("orders")}
              >
                My Orders
              </button>
              <button
                type="button"
                className="danger-tab"
                onClick={handleLogout}
              >
                Sign Out
              </button>
            </div>

            {accountError ? <p className="account-error">{accountError}</p> : null}

            <div className="account-panel-body">
              {activeAccountTab === "profile"
                ? renderProfileContent()
                : renderOrdersContent()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
